import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { DashboardWidget } from '../viewmodels/dashboard/dashboard-widget';
import { AssetHierarchyItem } from '../viewmodels/hierarchy/asset-hierarchy-item';
import { User } from '../viewmodels/login/user';
import { ViewModel } from '../viewmodels/view-model';
import { WidgetDataType } from '../views/dashboard/widgets/widget-data-type';
import { BackendServiceBase } from './backend-service-base';
import { CONFIG, Configuration } from './config';
import { ConfigService } from './config.service';
import { SpinnerService } from './spinner/spinner.service';

export interface Backend {
  getAll<T extends ViewModel>(genericType: new () => T): Observable<T[]>;
  getById<T extends ViewModel>(genericType: new () => T, id: string): Observable<T>;
  update<T extends ViewModel>(genericType: new () => T, id: string, object: T): Observable<T>;
  create<T extends ViewModel>(genericType: new () => T, object: T): Observable<T>;
  delete<T extends ViewModel>(genericType: new () => T, id: string): Observable<T>;
  deleteFromSpecifiedRoute<T extends ViewModel>(typeNameSingular: string, apiRoute: string, id: string): Observable<T>;
}

@Injectable({
  providedIn: 'root',
})
export class BackendService extends BackendServiceBase implements Backend {
  constructor(
    private http: HttpClient,
    public spinner: SpinnerService,
    private configService: ConfigService,
  ) {
    super();

    const configuration: Configuration = configService.getConfiguration();

    if (configuration?.apiBaseUrl) {
      this.apiUrl = configuration.apiBaseUrl;
    }

    if (configuration?.authBaseUrl) {
      this.identityUrl = configuration.authBaseUrl;
    }

    this.options.headers = this.getHeaders();
  }

  getAllAsync<T extends ViewModel>(genericType: new () => T): Promise<T[]> {
    const genericTypeInfo: T = new genericType();

    this.spinner.show();
    this.options.headers = this.getHeaders();

    return new Promise(resolve => {
      const list = this.http.get(this.apiUrl + '/' + genericTypeInfo.apiRoute, this.options).pipe(
        map((res: any[]) =>
          res.map(r => {
            const item = new genericType();

            item.loadFromJson(r);

            return item;
          }),
        ),
        catchError(err => throwError(this.handleError(`Could not retrieve all ${genericTypeInfo.friendlyTypeNameSingular} items.`, err))),
        finalize(() => this.spinner.hide()),
      );

      list.subscribe(items => {
        resolve(items);
      });
    });
  }

  getAllIdentity<T extends ViewModel>(genericType: new () => T): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    return this.getAllFromRemote<T>(genericType, this.identityUrl, genericTypeInfo.apiRoute);
  }

  getAllByPermission<T extends ViewModel>(genericType: new () => T, permissionPath: string): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    return this.getAllByPermissionFromRemote<T>(genericType, this.apiUrl, genericTypeInfo.apiRoute, true, permissionPath);
  }

  private getAllByPermissionFromRemote<T extends ViewModel>(
    genericType: new () => T,
    remoteUrl: string,
    apiRoute: string,
    showSpinner: boolean = true,
    permissionPath: string = '',
  ): Observable<T[]> {
    const genericTypeInfo: T = new genericType();


    if (showSpinner) {
      this.spinner.show();
    }

    return this.http
      .get(remoteUrl + '/' + apiRoute + '/' + CONFIG.apiRoutes.getAllByPermission, { params: { permissionPath }, headers: this.getHeaders() })
      .pipe(
        map((res: any[]) =>
          res.map(r => {
            const item = new genericType();

            item.loadFromJson(r);

            return item;
          }),
        ),
        catchError(err => this.handleError(`Could not retrieve all ${genericTypeInfo.friendlyTypeNameSingular} items.`, err)),
        finalize(() => {
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
      ) as Observable<T[]>;
  }

  getAll<T extends ViewModel>(genericType: new () => T): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    return this.getAllFromRemote<T>(genericType, this.apiUrl, genericTypeInfo.apiRoute);
  }

  getAllRoute<T extends ViewModel>(genericType: new () => T, apiRoute: string, showSpinner: boolean = true): Observable<T[]> {
    return this.getAllFromRemote<T>(genericType, this.apiUrl, apiRoute, showSpinner);
  }

  private getAllFromRemote<T extends ViewModel>(
    genericType: new () => T,
    remoteUrl: string,
    apiRoute: string,
    showSpinner: boolean = true,
  ): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    if (showSpinner) {
      this.spinner.show();
    }

    this.options.headers = this.getHeaders();

    return this.http.get(remoteUrl + '/' + apiRoute, this.options).pipe(
      map((res: any[]) =>
        res.map(r => {
          const item = new genericType();

          item.loadFromJson(r);

          return item;
        }),
      ),
      catchError(err => this.handleError(`Could not retrieve all ${genericTypeInfo.friendlyTypeNameSingular} items.`, err)),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    ) as Observable<T[]>;
  }

  getAllByIdIdentity<T extends ViewModel>(genericType: new () => T, id: string, apiRoute: string): Observable<T[]> {
    return this.getAllByIdFromRemote(this.identityUrl, genericType, id, apiRoute);
  }

  getAllById<T extends ViewModel>(genericType: new () => T, id: string, apiRoute: string, showSpinner: boolean = true): Observable<T[]> {
    return this.getAllByIdFromRemote(this.apiUrl, genericType, id, apiRoute, showSpinner);
  }

  getAllByIdAsync<T extends ViewModel>(genericType: new () => T, id: string, apiRoute: string): Promise<T[]> {
    return this.getAllByIdFromRemoteAsync(this.apiUrl, genericType, id, apiRoute);
  }

  public getAllFromRoute<T extends ViewModel>(obj: new () => T, api: string, id: string): Observable<T[]> {
    return this.getAllByIdFromRemote<T>(this.apiUrl, obj, id, api);
  }

  private getAllByIdFromRemote<T extends ViewModel>(
    remoteUrl: string,
    genericType: new () => T,
    id: string,
    apiRoute: string,
    showSpinner: boolean = true,
  ): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    if (showSpinner) {
      this.spinner.show();
    }

    this.options.headers = this.getHeaders();

    return this.http.get(remoteUrl + '/' + apiRoute + '/' + id, this.options).pipe(
      map((res: any[]) =>
        res.map(r => {
          const item = new genericType();

          item.loadFromJson(r);

          return item;
        }),
      ),
      catchError(err => this.handleError(`Could not retrieve all ${genericTypeInfo.friendlyTypeNameSingular} items with id ${id}.`, err)),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    ) as Observable<T[]>;
  }

  private getAllByIdFromRemoteAsync<T extends ViewModel>(remoteUrl: string, genericType: new () => T, id: string, apiRoute: string): Promise<T[]> {
    const genericTypeInfo: T = new genericType();

    this.spinner.show();
    this.options.headers = this.getHeaders();

    return new Promise(resolve => {
      const list = this.http.get(remoteUrl + '/' + apiRoute + '/' + id, this.options).pipe(
        map((res: any[]) =>
          res.map(r => {
            const item = new genericType();

            item.loadFromJson(r);

            return item;
          }),
        ),
        catchError(err =>
          throwError(this.handleError(`Could not retrieve all ${genericTypeInfo.friendlyTypeNameSingular} items with id ${id}.`, err)),
        ),
        finalize(() => this.spinner.hide()),
      );

      list.subscribe(items => {
        resolve(items);
      });
    });
  }

  getByIdFromUrl<T extends ViewModel>(genericType: new () => T, id: string, url: string): Observable<T> {
    return this.getByIdArrayRoute(genericType, [id], url);
  }

  getById<T extends ViewModel>(genericType: new () => T, id: string): Observable<T> {
    return this.getByIdArray(genericType, [id]);
  }

  public getByIdIdentity<T extends ViewModel>(genericType: new () => T, id: string, url?: string): Observable<T> {
    if (!url) {
      const genericTypeInfo: T = new genericType();

      url = genericTypeInfo.apiRoute;
    }

    return this.getByIdArrayRemote(this.identityUrl, genericType, [id], url);
  }

  public getBySearchParams<T extends ViewModel>(genericType: new () => T, httpParams: HttpParams): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.spinner.show();

    this.options.headers = this.getHeaders();
    this.options.params = httpParams;

    return this.http.get(this.apiUrl + '/' + genericTypeInfo.apiRoute, this.options).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not retrieve ${genericTypeInfo.friendlyTypeNameSingular}.`, err)),
      finalize(() => {
        this.options.params = null;
        this.spinner.hide();
      }),
    ) as Observable<T>;
  }

  /**
   * Performs a GET with a URL constructed from the generic type api route
   * followed by each id in the array as path parameters
   * @param genericType Type of the entity to get
   * @param ids Array of ids to use as path parameters in the URL
   * @param apiRoute Array of ids to use as path parameters in the URL
   */
  getByIdArrayRoute<T extends ViewModel>(
    genericType: new () => T,
    ids: string[],
    apiRoute: string,
    showSpinner: boolean = true,
    httpParams?: HttpParams,
  ): Observable<T> {
    const genericTypeInfo: T = new genericType();

    if (showSpinner) {
      this.spinner.show();
    }

    this.options.headers = this.getHeaders();
    this.options.params = httpParams;

    const id = ids.join('/');

    return this.http.get(this.apiUrl + '/' + apiRoute + '/' + id, this.options).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not retrieve ${genericTypeInfo.friendlyTypeNameSingular} with id '${id}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  getByIdArrayRemote<T extends ViewModel>(
    remoteUrl: string,
    genericType: new () => T,
    ids: string[],
    apiRoute: string,
    showSpinner: boolean = true,
    httpParams?: HttpParams,
  ): Observable<T> {
    const genericTypeInfo: T = new genericType();

    if (showSpinner) {
      this.spinner.show();
    }

    this.options.headers = this.getHeaders();
    this.options.params = httpParams;

    const id = ids.join('/');

    return this.http.get(remoteUrl + '/' + apiRoute + '/' + id, this.options).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not retrieve ${genericTypeInfo.friendlyTypeNameSingular} with id '${id}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  /**
   * Performs a GET with a URL constructed from the generic type api route
   * followed by each id in the array as path parameters
   * @param genericType Type of the entity to get
   * @param ids Array of ids to use as path parameters in the URL
   */
  public getByIdArray<T extends ViewModel>(genericType: new () => T, ids: string[]): Observable<T> {
    const genericTypeInfo: T = new genericType();

    return this.getByIdArrayRoute(genericType, ids, genericTypeInfo.apiRoute);
  }

  public get<T extends ViewModel>(genericType: new () => T): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.spinner.show();

    this.options.headers = this.getHeaders();

    return this.http.get(this.apiUrl + '/' + genericTypeInfo.apiRoute, this.options).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not retrieve ${genericTypeInfo.friendlyTypeNameSingular}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  getBooleanResponse(apiRoute: string): Observable<boolean> {
    this.options.headers = this.getHeaders();

    this.spinner.show();

    return this.http.get<boolean>(this.apiUrl + '/' + apiRoute, this.options).pipe(
      catchError(err => this.handleError('Could not retrieve boolean data', err)),
      finalize(() => this.spinner.hide()),
    );
  }

  getBulk<T extends ViewModel>(genericType: new () => T, ids: string[]): Observable<any> {
    const genericTypeInfo: T = new genericType();

    this.options.headers = this.getHeaders();

    return this.http.post(`${this.apiUrl}/${genericTypeInfo.apiRoute}/bulk/get`, JSON.stringify(ids), this.options).pipe(
      map((res: any[]) => res.map(item => {
        const lookup = new genericType();

        lookup.loadFromJson(item);

        return lookup;
      })),
      catchError(errorResponse => {
        this.handleError(`Error posting ${genericTypeInfo.friendlyTypeNameSingular} data`, errorResponse);

        return throwError(errorResponse.error);
      })
    ) as Observable<any>;
  }

  postObject<T extends ViewModel>(genericType: new () => T, object: any, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.options.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.post(this.apiUrl + '/' + genericTypeInfo.apiRoute, JSON.stringify(object), this.options).pipe(
      tap(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(errorResponse => {
        this.handleError('Error posting ' + genericTypeInfo.friendlyTypeNameSingular + ' data', errorResponse);

        return throwError(errorResponse.error);
      }),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    ) as Observable<T>;
  }

  postObjectRoute<T extends ViewModel>(apiRoute: string, object: any, showSpinner: boolean = true, params: any = {}): Observable<any> {
    this.options.headers = this.getHeaders();
    this.options.params = this.createHttpParams(params);

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.post(this.apiUrl + '/' + apiRoute, JSON.stringify(object), this.options).pipe(
      tap(res => res),
      catchError(errorResponse => {
        this.handleError('Error posting to ' + apiRoute, errorResponse);

        return throwError(errorResponse.error);
      }),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    );
  }

  postObjectList<T extends ViewModel>(genericType: new () => T, object: any): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    return this.postObjectListRoute(genericType, object, genericTypeInfo.apiRoute);
  }

  public postObjectListRoute<T extends ViewModel>(
    genericType: new () => T,
    object: any,
    apiRoute: string,
    showSpinner: boolean = true,
    params: any = {},
  ): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    this.options.headers = this.getHeaders();
    this.options.params = this.createHttpParams(params);

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.post(this.apiUrl + '/' + apiRoute, JSON.stringify(object), this.options).pipe(
      map((res: any[]) =>
        res.map(r => {
          const item = new genericType();

          item.loadFromJson(r);

          return item;
        }),
      ),
      catchError(errorResponse => {
        this.handleError('Error posting ' + genericTypeInfo.friendlyTypeNameSingular + ' data', errorResponse);

        return throwError(errorResponse.error);
      }),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    );
  }

  putObjectListRoute<T extends ViewModel>(genericType: new () => T, object: any, apiRoute: string, showSpinner: boolean = true): Observable<T[]> {
    const genericTypeInfo: T = new genericType();

    this.options.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.put(`${this.apiUrl}/${apiRoute}`, JSON.stringify(object), this.options).pipe(
      map((res: any[]) =>
        res.map(r => {
          const item = new genericType();

          item.loadFromJson(r);

          return item;
        }),
      ),
      catchError(errorResponse => {
        this.handleError(`Error updating ${genericTypeInfo.friendlyTypeNameSingular} data`, errorResponse);

        return throwError(errorResponse.error);
      }),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    );
  }

  getBooleanResponseForObject(apiRoute: string, object: any): Observable<boolean> {
    this.options.headers = this.getHeaders();

    this.spinner.show();

    return this.http.post<boolean>(this.apiUrl + '/' + apiRoute, JSON.stringify(object), this.options).pipe(
      catchError(err => this.handleError('Could not retrieve boolean data', err)),
      finalize(() => this.spinner.hide()),
    );
  }

  updateBooleanValueById(apiRoute: string, id: string, value: boolean): Observable<boolean> {
    this.options.headers = this.getHeaders();

    this.spinner.show();

    return this.http.put<boolean>(this.apiUrl + '/' + apiRoute + '/' + id, value, this.options).pipe(
      catchError(err => this.handleError('Could not update boolean value for route: ' + apiRoute, err)),
      finalize(() => this.spinner.hide()),
    );
  }

  updateAnyById(apiRoute: string, id: string, value: any): Observable<any> {
    this.options.headers = this.getHeaders();

    this.spinner.show();

    return this.http.put(this.apiUrl + '/' + apiRoute + '/' + id, value, this.options).pipe(
      catchError(err => this.handleError('Could not update value for route: ' + apiRoute, err)),
      finalize(() => this.spinner.hide()),
    );
  }

  updateAny(apiRoute: string, value: any): Observable<any> {
    this.options.headers = this.getHeaders();

    this.spinner.show();

    return this.http.put(this.apiUrl + '/' + apiRoute, value, this.options).pipe(
      catchError(err => this.handleError('Could not update value for route: ' + apiRoute, err)),
      finalize(() => this.spinner.hide()),
    );
  }

  public getAnyByIdArrayRoute(apiRoute: string, ids: string[], showSpinner: boolean = true): Observable<any> {
    this.options.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    let requestUrl = this.apiUrl + '/' + apiRoute;

    for (const id of ids) {
      requestUrl = requestUrl + '/' + id;
    }

    return this.http.get(requestUrl, this.options).pipe(
      catchError(err => this.handleError('Could not retrieve data from API route: ' + requestUrl, err)),
      finalize(() => this.spinner.hide()),
    );
  }

  public getAnyById(apiRoute: string, id: string): Observable<any> {
    return this.getAnyByIdArrayRoute(apiRoute, [id]);
  }

  public updateList<T extends ViewModel>(genericType: new () => T, object: T, showSpinner: boolean = true): Observable<T> {
    return this.updateRemote<T>(this.apiUrl, genericType, '-1', object, showSpinner);
  }

  public update<T extends ViewModel>(genericType: new () => T, id: string, object: T, showSpinner: boolean = true): Observable<T> {
    return this.updateRemote<T>(this.apiUrl, genericType, id, object, showSpinner);
  }

  public updateIdentity<T extends ViewModel>(genericType: new () => T, id: string, object: T, showSpinner: boolean = true): Observable<T> {
    return this.updateRemote<T>(this.identityUrl, genericType, id, object, showSpinner);
  }

  public updateRemote<T extends ViewModel>(
    remoteUrl: string = this.apiUrl,
    genericType: new () => T,
    id: string,
    object: T,
    showSpinner: boolean = true,
  ): Observable<T> {
    const genericTypeInfo: T = new genericType();

    if (showSpinner) {
      this.spinner.show();
    }

    this.headers = this.getHeaders();

    return this.http.put(remoteUrl + '/' + genericTypeInfo.apiRoute + '/' + id, JSON.stringify(object.json()), { headers: this.headers }).pipe(
      map(res => {
        if (res) {
          const item = new genericType();

          item.loadFromJson(res);

          return item;
        }
      }),
      catchError(err => this.handleError(`Could not update ${genericTypeInfo.friendlyTypeNameSingular} with id '${id}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  updateDashboardWidgetPositions(dashboardId: string, dashboardWidgets: DashboardWidget[]) {
    this.headers = this.getHeaders();
    const excludeWidgetDataProperty = (key: string, value: any) => (key === 'data' ? undefined : value);

    return this.http
      .put(
        this.apiUrl + '/dashboardWidget/UpdateDashboardWidgetPositions/' + dashboardId,
        JSON.stringify(dashboardWidgets, excludeWidgetDataProperty),
        { headers: this.headers },
      )
      .pipe(
        map(res => { }),
        catchError(err => this.handleError('Could not update dashboard widget positions.', err)),
        finalize(() => this.spinner.hide()),
      );
  }

  public createIdentity<T extends ViewModel>(genericType: new () => T, object: T): Observable<T> {
    return this.createRemote<T>(this.identityUrl, genericType, object);
  }

  public create<T extends ViewModel>(genericType: new () => T, object: T, showSpinner: boolean = true): Observable<T> {
    return this.createRemote<T>(this.apiUrl, genericType, object, showSpinner);
  }

  public copy<T extends ViewModel>(genericType: new () => T, object: T, copyFromId: string, showSpinner: boolean = true): Observable<T> {
    return this.copyRemote<T>(this.apiUrl, genericType, object, copyFromId, showSpinner);
  }

  public copyRemote<T extends ViewModel>(remoteUrl: string, genericType: new () => T, object: T, copyFrom: string, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.post(`${remoteUrl}/${genericTypeInfo.apiRoute}/copy/${copyFrom}`, JSON.stringify(object.json()), { headers: this.headers }).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not copy ${genericTypeInfo.friendlyTypeNameSingular}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  public createRemote<T extends ViewModel>(remoteUrl: string, genericType: new () => T, object: T, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.post(remoteUrl + '/' + genericTypeInfo.apiRoute, JSON.stringify(object.json()), { headers: this.headers }).pipe(
      map(res => {
        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(err => this.handleError(`Could not create a new ${genericTypeInfo.friendlyTypeNameSingular}.`, err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<T>;
  }

  createDashboardWidget(dashboardId: string, widgetDataType: WidgetDataType): Observable<DashboardWidget> {
    const dashboardWidget = new DashboardWidget();
    this.headers = this.getHeaders();
    this.spinner.show();

    return this.http
      .post(this.apiUrl + '/' + dashboardWidget.apiRoute + '/' + dashboardId, JSON.stringify(widgetDataType), { headers: this.headers })
      .pipe(
        map(res => {
          const item = new DashboardWidget();

          item.loadFromJson(res);

          return item;
        }),
        catchError(err => this.handleError(`Could not create a new ${dashboardWidget.friendlyTypeNameSingular}.`, err)),
        finalize(() => this.spinner.hide()),
      ) as Observable<DashboardWidget>;
  }

  deleteRange<T extends ViewModel>(genericType: new () => T, ids: string[], showSpinner: boolean = true): Observable<boolean> {
    return this.deleteRangeObject<T>(genericType, ids, showSpinner);
  }

  deleteRangeObject<T extends ViewModel>(genericType: new () => T, object: object, showSpinner: boolean = true): Observable<boolean> {
    const route = new genericType().apiRoute;

    if (showSpinner) {
      this.spinner.show();
    }

    this.options.headers = this.getHeaders();
    this.options.params = new HttpParams();

    return this.http.post<boolean>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.deleteRange, JSON.stringify(object), this.options).pipe(
      map((res: boolean) => res),
      catchError(err => this.handleError('Delete operation is failed.', err)),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    );
  }


  delete<T extends ViewModel>(genericType: new () => T, id: string, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    return this.deleteRemote(this.apiUrl, genericTypeInfo.friendlyTypeNameSingular, genericTypeInfo.apiRoute, id, showSpinner);
  }

  deleteFromSpecifiedRoute<T extends ViewModel>(typeNameSingular: string, apiRoute: string, id: string, showSpinner: boolean = true): Observable<T> {
    return this.deleteRemote(this.apiUrl, typeNameSingular, apiRoute, id, showSpinner);
  }

  deleteIdentity<T extends ViewModel>(genericType: new () => T, id: string, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    return this.deleteRemote(this.identityUrl, genericTypeInfo.friendlyTypeNameSingular, genericTypeInfo.apiRoute, id, showSpinner);
  }

  deleteRemote<T extends ViewModel>(
    remoteUrl: string,
    typeNameSingular: string,
    apiRoute: string,
    id: string,
    showSpinner: boolean = true,
  ): Observable<T> {
    this.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http
      .delete(remoteUrl + '/' + apiRoute + '/' + id, {
        headers: this.headers,
      })
      .pipe(
        map(res => {
          const item = res;

          return item || {};
        }),
        catchError(err => {
          this.handleError(`Could not delete ${typeNameSingular}.`, err);

          return throwError(err.error);
        }),
        finalize(() => this.spinner.hide()),
      ) as Observable<T>;
  }

  uploadDocument(id: string, name: string, fileToUpload: any, apiRoute: string): Observable<any> {
    this.spinner.show();

    const input = new FormData();

    input.append('id', id);
    input.append('name', name);
    input.append('file', fileToUpload);

    return this.http.post(this.apiUrl + '/' + apiRoute, input).pipe(
      catchError(err => this.handleError('Could not upload a new Media Item.', err)),
      finalize(() => this.spinner.hide()),
    );
  }

  updateDocument(id: string, name: string, fileToUpload: any, apiRoute: string): Observable<any> {
    const input = new FormData();

    input.append('id', id);
    input.append('name', name);
    input.append('file', fileToUpload);


    this.spinner.show();

    return this.http.put(this.apiUrl + '/' + apiRoute, input).pipe(
      catchError(err => this.handleError('Could not update a Media Item.', err)),
      finalize(() => this.spinner.hide()),
    );
  }


  resetUserPassword(userName: string, passwords: string[], showSpinner: boolean = true): Observable<User> {
    if (showSpinner) {
      this.spinner.show();
    }

    this.headers = this.getHeaders();

    return this.http
      .put(this.identityUrl + '/' + CONFIG.apiRoutes.resetUserPassword + '/' + userName, JSON.stringify(passwords), { headers: this.headers })
      .pipe(
        map(user => {
          const item = new User();

          item.loadFromJson(user);

          return item;
        }),
        catchError(err => this.handleError(`Could not update password for user '${userName}.`, err)),
        finalize(() => this.spinner.hide()),
      ) as Observable<User>;
  }

  public getRootLevelHierarchy(selectedId: string = null): Observable<AssetHierarchyItem[]> {
    this.spinner.show();
    this.headers = this.getHeaders();

    return this.http
      .get<AssetHierarchyItem[]>(this.apiUrl + '/' + CONFIG.apiRoutes.hierarchyTreeRoot, { params: { selectedId }, headers: this.headers })
      .pipe(
        map(res =>
          res.map(r => {
            const item = new AssetHierarchyItem();

            item.loadFromJson(r);

            return item;
          }),
        ),
        catchError(err => this.handleError('Could not obtain root level of hierarchy', err)),
        finalize(() => this.spinner.hide()),
      ) as Observable<AssetHierarchyItem[]>;
  }

  getChildrenHierarchy(parentId: string): Observable<AssetHierarchyItem[]> {
    this.spinner.show();
    this.headers = this.getHeaders();

    return this.http
      .get<AssetHierarchyItem[]>(this.apiUrl + '/' + CONFIG.apiRoutes.hierarchyTreeChildren, { params: { parentId }, headers: this.headers })
      .pipe(
        map(res =>
          res.map(r => {
            const item = new AssetHierarchyItem();

            item.loadFromJson(r);

            return item;
          }),
        ),
        catchError(err => this.handleError('Could not obtain root level of hierarchy', err)),
        finalize(() => this.spinner.hide()),
      ) as Observable<AssetHierarchyItem[]>;
  }

  getFilteredHierarchy(filterBy: { key: string; value: string }[]): Observable<AssetHierarchyItem[]> {
    this.spinner.show();
    this.headers = this.getHeaders();
    const params = {};

    this.addDictionaryToParameters(params, 'filterBy', filterBy);

    return this.http.get<AssetHierarchyItem[]>(this.apiUrl + '/' + CONFIG.apiRoutes.hierarchyFilterNodes, { params, headers: this.headers }).pipe(
      map(res =>
        res.map(r => {
          const item = new AssetHierarchyItem();

          item.loadFromJson(r);

          return item;
        }),
      ),
      catchError(err => this.handleError('Could not obtain root level of hierarchy', err)),
      finalize(() => this.spinner.hide()),
    ) as Observable<AssetHierarchyItem[]>;
  }

  getItemsCount(apiRoute: string, filterBy: { key: string; value: string }[] = null): Observable<number> {
    this.spinner.show();
    this.headers = this.getHeaders();
    const params = {};

    if (filterBy) {
      this.addDictionaryToParameters(params, 'filterBy', filterBy);
    }

    return this.http
      .get<number>(this.apiUrl + '/' + apiRoute, {
        params,
        headers: this.headers,
      })
      .pipe(
        catchError(err => this.handleError('Could not obtain root level of hierarchy', err)),
        finalize(() => this.spinner.hide()),
      ) as Observable<number>;
  }

  private addDictionaryToParameters(params: any, keyword: string, filterBy: { key: string; value: string }[]) {
    filterBy.forEach(filter => {
      if (!!filter.value && filter.value !== '') {
        params[`${keyword}[${filter.key}]`] = filter.value;
      }
    });
  }

  public putObjectRoute<T extends ViewModel>(genericType: new () => T, apiRoute: string, id: string, object: any, showSpinner: boolean = true): Observable<T> {
    const genericTypeInfo: T = new genericType();

    this.options.headers = this.getHeaders();

    if (showSpinner) {
      this.spinner.show();
    }

    return this.http.put(`${this.apiUrl}/${apiRoute}/${id}`, JSON.stringify(object), this.options).pipe(
      map(res => {

        const item = new genericType();

        item.loadFromJson(res);

        return item;
      }),
      catchError(errorResponse => {

        this.handleError(`Error updating ${genericTypeInfo.friendlyTypeNameSingular} data`, errorResponse);

        return throwError(errorResponse.error);
      }),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    );
  }
}
